import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Divider } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { fetchPointHistory, fetchOrderHistory, fetchReceiptHistory } from '../api/auth';
import { useAuth } from '../auth/AuthContext';
import type { OrderItem, ReceiptSubmissionItem } from '../api/types';
import type { PointHistoryItem } from '../types/api';

const PREVIEW_COUNT = 3;

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatCents(cents: number | null | undefined) {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function SectionHeader({ title, onSeeAll, expanded }: { title: string; onSeeAll?: () => void; expanded?: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#1A1A1A' }}>{title}</Typography>
      {onSeeAll && (
        <Typography
          component="button"
          onClick={onSeeAll}
          sx={{
            fontSize: 13, color: '#7BA88A', fontWeight: 600, border: 'none',
            background: 'none', cursor: 'pointer', p: 0, fontFamily: 'inherit',
          }}
        >
          {expanded ? 'Show less' : 'See all'}
        </Typography>
      )}
    </Box>
  );
}

function EmptyState({ label }: { label: string }) {
  return <Typography sx={{ fontSize: 13, color: '#AAA', py: 1.5, textAlign: 'center' }}>{label}</Typography>;
}

function LoadingState() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
      <CircularProgress size={20} sx={{ color: '#7BA88A' }} />
    </Box>
  );
}

function PointsSection({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['points-history', 0, 20],
    queryFn: () => fetchPointHistory(0, 20),
  });

  const items: PointHistoryItem[] = data?.content ?? [];
  const displayed = expanded ? items : items.slice(0, PREVIEW_COUNT);
  const hasMore = items.length > PREVIEW_COUNT;

  const actionColor = (action: string) => ['EARN', 'REFUND'].includes(action) ? '#2E7D32' : '#C62828';
  const actionSign = (action: string) => ['EARN', 'REFUND'].includes(action) ? '+' : '-';

  return (
    <Box>
      <SectionHeader title="Points History" onSeeAll={hasMore ? onToggle : undefined} expanded={expanded} />
      {isLoading ? <LoadingState /> : items.length === 0 ? <EmptyState label="No points history yet" /> : (
        displayed.map((item, i) => (
          <Box key={item.transactionId ?? i}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: '10px', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }} noWrap>
                  {item.description ?? item.source ?? item.action}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#888', mt: '2px' }}>{formatDate(item.createdAt)}</Typography>
              </Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: actionColor(item.action), whiteSpace: 'nowrap' }}>
                {actionSign(item.action)}{item.points} pts
              </Typography>
            </Box>
            {i < displayed.length - 1 && <Divider />}
          </Box>
        ))
      )}
    </Box>
  );
}

function OrdersSection({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['order-history', 0, 20],
    queryFn: () => fetchOrderHistory(0, 20),
  });

  const items: OrderItem[] = data?.content ?? [];
  const displayed = expanded ? items : items.slice(0, PREVIEW_COUNT);
  const hasMore = items.length > PREVIEW_COUNT;

  const channelLabel = (ch: string | null) => {
    if (ch === 'EATS365') return 'POS Order';
    if (ch === 'STAFF_MANUAL') return 'In-store';
    return ch ?? 'Order';
  };

  return (
    <Box>
      <SectionHeader title="Order History" onSeeAll={hasMore ? onToggle : undefined} expanded={expanded} />
      {isLoading ? <LoadingState /> : items.length === 0 ? <EmptyState label="No orders yet" /> : (
        displayed.map((item, i) => (
          <Box key={item.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: '10px', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }} noWrap>
                  {channelLabel(item.channel)}{item.orderRef ? ` · ${item.orderRef}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', gap: '6px', mt: '2px', flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 11, color: '#888' }}>{formatDate(item.occurredAt)}</Typography>
                  {item.couponCode && (
                    <Typography sx={{ fontSize: 11, color: '#7BA88A' }}>🏷 {item.couponCode}</Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1A1A1A', whiteSpace: 'nowrap' }}>
                  {formatCents(item.revenueCents)}
                </Typography>
                {(item.couponDiscountCents || item.pointsDiscountCents) && (
                  <Typography sx={{ fontSize: 11, color: '#7BA88A' }}>
                    -{formatCents((item.couponDiscountCents ?? 0) + (item.pointsDiscountCents ?? 0))} off
                  </Typography>
                )}
              </Box>
            </Box>
            {i < displayed.length - 1 && <Divider />}
          </Box>
        ))
      )}
    </Box>
  );
}

function ReceiptsSection({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['receipt-history', 0, 20],
    queryFn: () => fetchReceiptHistory(0, 20),
  });

  const items: ReceiptSubmissionItem[] = data?.content ?? [];
  const displayed = expanded ? items : items.slice(0, PREVIEW_COUNT);
  const hasMore = items.length > PREVIEW_COUNT;

  const statusColor = (status: string) => {
    if (status === 'APPROVED') return '#2E7D32';
    if (status === 'REJECTED') return '#C62828';
    return '#E8A84A';
  };

  return (
    <Box>
      <SectionHeader title="Receipt Submissions" onSeeAll={hasMore ? onToggle : undefined} expanded={expanded} />
      {isLoading ? <LoadingState /> : items.length === 0 ? <EmptyState label="No receipts submitted" /> : (
        displayed.map((item, i) => (
          <Box key={item.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: '10px', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }} noWrap>
                  {item.storeName ?? 'Receipt'}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#888', mt: '2px' }}>{formatDate(item.createdAt)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1A1A1A', whiteSpace: 'nowrap' }}>
                  {formatCents(item.totalAmountCents)}
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: statusColor(item.status) }}>
                  {item.status}{item.pointsAwarded != null ? ` · +${item.pointsAwarded} pts` : ''}
                </Typography>
              </Box>
            </Box>
            {i < displayed.length - 1 && <Divider />}
          </Box>
        ))
      )}
    </Box>
  );
}

export default function TransactionHistoryPage() {
  const navigate = useNavigate();
  const { customer } = useAuth();

  const [pointsExpanded, setPointsExpanded] = useState(false);
  const [ordersExpanded, setOrdersExpanded] = useState(false);
  const [receiptsExpanded, setReceiptsExpanded] = useState(false);

  const pointsBalance = customer?.pointsBalance ?? 0;
  const totalSpent = customer?.totalSpending != null ? `$${Number(customer.totalSpending).toFixed(2)}` : '—';

  return (
    <Box sx={{ flex: 1, backgroundColor: '#F7F7F7', pb: '30px' }}>
      {/* Header */}
      <Box sx={{
        px: 2, pt: 2, pb: '14px', backgroundColor: '#fff',
        borderBottom: '1px solid #EAEAEA',
        display: 'flex', alignItems: 'center', gap: 1,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <Box
          component="button"
          onClick={() => navigate(-1)}
          sx={{
            border: 'none', background: 'none', cursor: 'pointer', p: '4px',
            display: 'flex', alignItems: 'center', color: '#1A1A1A',
          }}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Box>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#1A1A1A' }}>Transaction History</Typography>
      </Box>

      {/* Summary cards */}
      <Box sx={{ display: 'flex', gap: '10px', px: 2, pt: '14px', pb: '6px' }}>
        <Box sx={{
          flex: 1, backgroundColor: '#fff', borderRadius: '14px', p: '14px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: '4px',
        }}>
          <Typography sx={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Points Balance</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#7BA88A', lineHeight: 1.1 }}>{pointsBalance.toLocaleString()}</Typography>
          <Typography sx={{ fontSize: 11, color: '#AAA' }}>pts</Typography>
        </Box>
        <Box sx={{
          flex: 1, backgroundColor: '#fff', borderRadius: '14px', p: '14px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: '4px',
        }}>
          <Typography sx={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Amount Spent</Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1A1A1A', lineHeight: 1.1 }}>{totalSpent}</Typography>
          <Typography sx={{ fontSize: 11, color: '#AAA' }}>lifetime</Typography>
        </Box>
      </Box>

      {/* Sections */}
      <Box sx={{ px: 2, pt: '8px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Box sx={{ backgroundColor: '#fff', borderRadius: '14px', p: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <PointsSection expanded={pointsExpanded} onToggle={() => setPointsExpanded(e => !e)} />
        </Box>
        <Box sx={{ backgroundColor: '#fff', borderRadius: '14px', p: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <OrdersSection expanded={ordersExpanded} onToggle={() => setOrdersExpanded(e => !e)} />
        </Box>
        <Box sx={{ backgroundColor: '#fff', borderRadius: '14px', p: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <ReceiptsSection expanded={receiptsExpanded} onToggle={() => setReceiptsExpanded(e => !e)} />
        </Box>
      </Box>
    </Box>
  );
}
